import { useCallback, useMemo } from "react";
import { showErrorToast } from "@src/utils/toast";
import { useUsersAndPermissionsData } from "./useUsersAndPermissionsData";

/**
 * Centralises the pre-flight checks shared by destructive actions on the
 * Users & Permissions screens (revoke access, remove organisation admin,
 * …). Callers get a single `isSelf` flag to drive disabled/hidden UI plus
 * a `guard()` backup that short-circuits the action with a toast when the
 * caller is viewing themselves or the combined data isn't ready.
 */
export function useSelfActionGuard(targetUserId: string | undefined) {
  const { identity, isReady, isError } = useUsersAndPermissionsData();

  const isSelf = useMemo(() => {
    if (!targetUserId || !identity) return false;
    return targetUserId === String(identity.userId);
  }, [identity, targetUserId]);

  /**
   * Defence-in-depth for the rare case where a destructive trigger becomes
   * reachable before identity has settled — callers should still hide the
   * trigger via `isSelf` whenever possible.
   */
  const guard = useCallback((): boolean => {
    if (!isReady || isError) {
      showErrorToast({ message: "Something went wrong, please try again." });
      return true;
    }
    if (isSelf) {
      showErrorToast({ message: "You can't remove yourself." });
      return true;
    }
    return false;
  }, [isReady, isError, isSelf]);

  return { isSelf, guard };
}
